import React from 'react';
import { Form } from 'react-bootstrap';
import styled, { css } from 'styled-components';
import { t } from 'ttag';
import { ErrorMessage, Question } from './styles';

export type PickerLocation = {
    id: string;
    name: string;
    address: string;
    open: boolean;
    // when it's open, or that it's not taking orders
    status: string;
    // in miles, once there's somewhere to measure from
    distance?: number;
};

type LocationPickerProps = {
    question: string;
    // what the list is of: Restaurants
    label: string;
    noun: { one: string; many: string };
    locations: PickerLocation[];
    // how many there are to choose from, before any search
    total: number;
    selected: string | null;
    onSelect: (id: string) => void;

    // without these, there's no search
    query?: string;
    onQuery?: (query: string) => void;
    placeholder?: string;
    onUseLocation?: () => void;
    locating?: boolean;
    error?: string;
};

// What there is to say of a location, as the rows of the list and the one chosen from it have it
export const LocationSummary = ({ location }: { location: PickerLocation }) => (
    <>
        <span className="details">
            <span className="name">{location.name}</span>
            <span className="address">{location.address}</span>
            <span className={`status ${location.open ? '' : 'closed'}`}>
                <i aria-hidden="true" />
                {location.status}
            </span>
        </span>
        <span className="distance">
            { location.distance !== undefined ? `${Math.round(location.distance * 10) / 10} mi` : '' }
        </span>
    </>
);

const LocationPicker = ({
    question,
    label,
    noun,
    locations,
    total,
    selected,
    onSelect,
    query = '',
    onQuery,
    placeholder,
    onUseLocation,
    locating = false,
    error,
}: LocationPickerProps) => {
    const count = locations.length;
    const countText = count < total ? `${count} of ${total} ${noun.many}` : `${count} ${count === 1 ? noun.one : noun.many}`;

    return (
        <>
            <Question>{question}</Question>

            { onQuery && (
                <SearchWrapper>
                    <Form.Control
                        size="lg"
                        type="text"
                        aria-label={placeholder}
                        placeholder={placeholder}
                        value={query}
                        onChange={(e) => onQuery(e.target.value)}
                    />
                    <span className="material-symbols-outlined search-icon" aria-hidden="true">search</span>
                    { query.length > 0 && (
                        <button type="button" className="clear" aria-label={t`Clear search`} onClick={() => onQuery('')}>
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    )}
                </SearchWrapper>
            )}

            { onUseLocation && (
                <UseLocation type="button" className="use-my-location" onClick={onUseLocation} disabled={locating}>
                    <span className="material-symbols-outlined" aria-hidden="true">near_me</span>
                    <span className="label">{locating ? t`Finding you…` : t`Use my location`}</span>
                </UseLocation>
            )}

            { error && <ErrorMessage role="alert">{error}</ErrorMessage> }

            <Count aria-live="polite">{countText}</Count>

            <List role="radiogroup" aria-label={label}>
                { count === 0 && (
                    <Hint>{query.trim() ? t`Nothing matches “${query.trim()}”.` : t`There is nowhere to choose from.`}</Hint>
                )}

                { locations.map((location) => (
                    <Location key={location.id}>
                        <label>
                            <input
                                type="radio"
                                name="location"
                                value={location.id}
                                checked={selected === location.id}
                                onChange={() => onSelect(location.id)}
                            />
                            <LocationSummary location={location} />
                        </label>
                    </Location>
                ))}
            </List>
        </>
    );
};

const SearchWrapper = styled.div`
    position: relative;

    .form-control {
        padding: 18px 48px 18px 48px;
        text-overflow: ellipsis;
    }

    .search-icon {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        padding: 4px;
        color: var(--text-black);
        font-size: 24px;
        line-height: 24px;
        pointer-events: none;
    }

    .clear {
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        display: flex;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--text-grey);
        font-size: 16px;
        line-height: 24px;
    }
`;

const UseLocation = styled.button`
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    border: 0;
    background: none;
    color: inherit;
    cursor: pointer;

    .label {
        text-decoration: underline;
        text-underline-offset: 4px;
    }

    &:disabled {
        cursor: default;
        color: var(--text-grey);

        .label {
            text-decoration: none;
        }
    }

    .material-symbols-outlined {
        font-size: 18px;
        line-height: 18px;
    }
`;

const Hint = styled.p`
    margin: 0;
    color: var(--text-grey);
`;

const Count = styled.p`
    margin: 4px 0 0 0;
    color: var(--text-grey);
    font-size: 13px;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
`;

const summaryStyles = css`
    .details {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    .name {
        font-family: var(--text_heading_font);
        letter-spacing: var(--text_heading_font_spacing);
        font-weight: bold;
        line-height: 24px;
    }

    .address {
        color: var(--text-grey);
        font-size: 14px;
        line-height: 20px;
    }

    .status {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        line-height: 20px;

        i {
            flex: none;
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #2E8B4E;
        }

        &.closed {
            color: var(--text-grey);

            i {
                background: var(--text-grey);
            }
        }
    }

    .distance {
        color: var(--text-grey);
        font-size: 14px;
        line-height: 24px;
        white-space: nowrap;
    }
`;

const Location = styled.div`
    margin: 0 -24px;
    padding: 0 24px;
    border-top: var(--ui_card_border);

    @media screen and (max-width: 767px) {
        margin: 0 -16px;
        padding: 0 16px;
    }

    /* 6% darker than whatever it is on */
    &:hover {
        background: rgb(0 0 0 / 6%);
    }

    label {
        display: grid;
        grid-template-columns: 20px minmax(0, 1fr) auto;
        gap: 14px;
        align-items: start;
        margin: 0;
        padding: 14px 0;
        cursor: pointer;
    }

    input[type='radio'] {
        appearance: none;
        -webkit-appearance: none;
        display: grid;
        place-items: center;
        width: 20px;
        height: 20px;
        margin: 2px 0 0 0;
        border: 2px solid var(--text-black);
        border-radius: 50%;
        background: #FFFFFF;
        cursor: pointer;

        &:checked {
            border-color: var(--ui_primary_cta);
        }

        &:checked::after {
            content: '';
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: var(--ui_primary_cta);
        }

        &:focus-visible {
            outline: 3px solid var(--text-black);
            outline-offset: 2px;
        }
    }

    &:hover input[type='radio'] {
        border-color: var(--ui_primary_cta);
    }

    ${summaryStyles}
`;

// The location chosen from the list, as a row of it is but without the radio button
export const PickedLocation = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 14px;
    align-items: start;
    padding: 14px 0;
    border-top: var(--ui_card_border);
    border-bottom: var(--ui_card_border);

    ${summaryStyles}
`;

export default LocationPicker;
